import { useEffect, useRef } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useProgramPanel } from "./programPanel";

const CHORD_MS = 1000;

const isTyping = (t: EventTarget | null) =>
  t instanceof HTMLElement && (t.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName));

/**
 * Keyboard shortcuts for the signed-in app: "/" search, "n" new case, "g c" cases, "g f" follow-ups (second key within
 * 1 s), Esc closes the right panel. Ignored while a field is focused or a modifier key is held.
 */
export function useShortcuts() {
  const navigate = useNavigate();
  const panel = useProgramPanel();
  // Held in a ref so a re-render mid-chord (a new panel value) does not tear down the listener and forget the "g".
  const close = useRef(panel.close);
  close.current = panel.close;
  const go = useRef(navigate);
  go.current = navigate;
  useEffect(() => {
    let chordAt = 0;
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === "Escape") { if (!e.defaultPrevented) close.current(); return; }
      if (isTyping(e.target)) return;
      const chord = chordAt && Date.now() - chordAt <= CHORD_MS;
      chordAt = 0;
      if (chord && e.key === "c") { go.current({ to: "/cases" }); return; }
      if (chord && e.key === "f") { go.current({ to: "/followups" }); return; }
      if (e.key === "g") { chordAt = Date.now(); return; }
      if (e.key === "n") { go.current({ to: "/cases/new" }); return; }
      if (e.key === "/") {
        const box = document.querySelector<HTMLInputElement>('input[role="combobox"][aria-label^="Search programs"]');
        if (box) { e.preventDefault(); box.focus(); }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
}
